import { Autocomplete, Box, Dialog, DialogActions, DialogContent, FormHelperText, TextField } from "@mui/material";
import Grid from '@mui/material/Grid';
import React, { useEffect } from "react";
import { CustomDialogTitle } from '../../../../Common/Custom/CustomDialogTitle';
import { MDBox } from '../../../../../Component/Common/Components/MDBox';
import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MDButton } from '../../../../../Component/Common/Components/MDButton';
import { EEOccupationalLevel } from '../../../../../Component/Model/Enum/EmploymentEquity';
import { MDTypography } from '../../../../../Component/Common/Components/MDTypography';
import { SmallLoading } from '../../../../../Component/Common/Components/Loading/IntermediateLoading';
import { PortalDocumentsDropZone } from '../../../../Common/PortalDocuments/PortalDocumentsDropZone';
import { toast } from "react-toastify";
import { projectedHeadcountSchema } from "./ProjectedHeadcountValidation";
import { ProjectedHeadcount } from "../../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/ProjectedHeadcount/ProjectedHeadcount";
import { ProjectedHeadcountService } from "../../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/ProjectedHeadcountService";
import AppTextInput from "../../../../../Component/Common/Components/FormValidationInputs/AppTextInput";
import { Quill } from "../../../../../Component/Common/Components/Quill/Quill";
import YearlyEmployeeInput from "./YearlyEmployeeInput";
import { decimalFormatter } from "../../../../../Helpers/DecimalFormatter";
import { ProjectedHeadcountSelectedMethod } from "../../../../../Component/Model/Enum/ProjectedHeadcountMethod";
import { ProjectedHeadcountFieldType } from "../../../../../Component/Model/Enum/ProjectedHeadcountFieldType";
import { EEScorecardAtom } from "../../../../../State/EEScorecard.Atom";
import { useRecoilValue } from "recoil";
import { ConsolidationAtom } from "../../../../../State/Consolidation.Atom";

interface IProps {
    handleCancel: () => void;
    id?: number;
    occupationalLevel: EEOccupationalLevel;
    isDisabled: boolean;
    scorecardId: string;
    occupationalLevelTitle: string;
    baselineHeadcount: number;
}

interface ISelectedMethodOptions {
    label: string;
    value: number;
}

const selectedMethodOptions: ISelectedMethodOptions[] = [
    { label: "Year 5 (2030 Total Growth)", value: 1 },
    { label: "Annual Growth Model", value: 2 }
];

export function calculateYear5TotalGrowthFromEmployees(
    numberEmployees: number,
    baselineEmployees: number
): number {
    const totalGrowth = (numberEmployees - baselineEmployees) / baselineEmployees * 100;
    return totalGrowth;
}

export function calculateYear5NumberOfEmployeesFromTotalGrowth(
    totalGrowth: number,
    baselineEmployees: number
): number {
    const numberOfEmployees = ((totalGrowth / 100) * baselineEmployees) + baselineEmployees;
    return numberOfEmployees;
}

export function CalculateYearlyNumberOfEmployees(
    annualGrowthPercent: number,
    previousYearEmployees: number
): number {
    const numberEmployeesCurrentYear = previousYearEmployees * (1 + annualGrowthPercent / 100);
    return numberEmployeesCurrentYear;
}

export function CalculateYearlyAnnualGrowth(
    numberEmployeesCurrentYear: number,
    numberEmployeesPreviousYear: number
): number { 
    const annualGrowth = ((numberEmployeesCurrentYear - numberEmployeesPreviousYear)/ numberEmployeesPreviousYear) * 100;
    return annualGrowth;
}

export const ProjectedHeadcountModal = (props: IProps) => {
    const scorecard = useRecoilValue(EEScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);
    const [isLoading, setIsLoading] = React.useState<boolean>(props.id !== undefined);
    const [projectedHeadcountId, setProjectedHeadcountId] = React.useState<number | undefined>(props.id);
    const commentConsolidatedMaxLength = 6000 * (scorecard.ConsolidationSourceScorecardsCount ?? 1);
    const commentMaxLength = isConsolidatedScorecard ? commentConsolidatedMaxLength : 6000;
    const [remainingNoteCharacters, setRemainingNoteCharacters] = React.useState(commentMaxLength);
    const [selectedMethodId, setSelectedMethodId]= React.useState<number>();
    
    const { handleSubmit, control, formState: { errors }, setValue, getValues, reset, watch} = useForm({
        mode: 'all',
        resolver: yupResolver(projectedHeadcountSchema(commentMaxLength))
    });

    const getProjectedHeadcount = async (id: number) => {
        try {
            const result = await ProjectedHeadcountService.GetProjectedHeadcount(id);
            
            const formData = {
                ...result.data,
                baselineHeadcount: props.baselineHeadcount,
                expectedNumberOfEmployeesYear1: result.data.expectedNumberOfEmployeesYear1 ?? 0,
                expectedNumberOfEmployeesYear2: result.data.expectedNumberOfEmployeesYear2 ?? 0,
                expectedNumberOfEmployeesYear3: result.data.expectedNumberOfEmployeesYear3 ?? 0,
                expectedNumberOfEmployeesYear4: result.data.expectedNumberOfEmployeesYear4 ?? 0,
                expectedNumberOfEmployeesYear5: result.data.expectedNumberOfEmployeesYear5 ?? 0,
                expectedPercentageGrowthYear1: result.data.expectedPercentageGrowthYear1 ?? ('N/A' as any),
                expectedPercentageGrowthYear2: result.data.expectedPercentageGrowthYear2 ?? ('N/A' as any),
                expectedPercentageGrowthYear3: result.data.expectedPercentageGrowthYear3 ?? ('N/A' as any),
                expectedPercentageGrowthYear4: result.data.expectedPercentageGrowthYear4 ?? ('N/A' as any),
                expectedPercentageGrowthYear5: result.data.expectedPercentageGrowthYear5 ?? ('N/A' as any),
            };
            
            reset(formData);
            setSelectedMethodId(result.data.selectedMethod);
        } 
        catch (error) {
            console.log(error);
        }
    };

    const handleDataSubmit: SubmitHandler<FieldValues> =  async(data: FieldValues) => {
        setIsLoading(true);
        try{
            if (Number(data.selectedMethod) === ProjectedHeadcountSelectedMethod.Year5){
                data.expectedNumberOfEmployeesYear1 = null;
                data.expectedNumberOfEmployeesYear2 = null;
                data.expectedNumberOfEmployeesYear3 = null;
                data.expectedNumberOfEmployeesYear4 = null;

                data.expectedPercentageGrowthYear1 = null;
                data.expectedPercentageGrowthYear2 = null;
                data.expectedPercentageGrowthYear3 = null;
                data.expectedPercentageGrowthYear4 = null;
            }

            const projectedHeadcount: ProjectedHeadcount.Dto = {
                eeOccupationalLevel: props.occupationalLevel,
                scorecardId: props.scorecardId,
                comment: data.comment,
                isDisabled: props.isDisabled,
                baselineHeadcount: data.baselineHeadcount,
                projectedHeadcount: data.projectedHeadcount,
                selectedMethod: data.selectedMethod?.value ?? selectedMethodId,
                expectedNumberOfEmployeesYear1: data.expectedNumberOfEmployeesYear1,
                expectedNumberOfEmployeesYear2: data.expectedNumberOfEmployeesYear2,
                expectedNumberOfEmployeesYear3: data.expectedNumberOfEmployeesYear3,
                expectedNumberOfEmployeesYear4: data.expectedNumberOfEmployeesYear4,
                expectedNumberOfEmployeesYear5: data.expectedNumberOfEmployeesYear5,
                expectedPercentageGrowthYear1: data.expectedPercentageGrowthYear1 === 'N/A'
                    ? null
                    : data.expectedPercentageGrowthYear1,
                expectedPercentageGrowthYear2: data.expectedPercentageGrowthYear2 === 'N/A'
                    ? null
                    : data.expectedPercentageGrowthYear2,
                expectedPercentageGrowthYear3: data.expectedPercentageGrowthYear3 === 'N/A'
                    ? null
                    : data.expectedPercentageGrowthYear3,
                expectedPercentageGrowthYear4: data.expectedPercentageGrowthYear4 === 'N/A'
                    ? null
                    : data.expectedPercentageGrowthYear4,
                expectedPercentageGrowthYear5: data.expectedPercentageGrowthYear5 === 'N/A'
                    ? null
                    : data.expectedPercentageGrowthYear5,
            }

            if(projectedHeadcountId == undefined) {
                const result = await ProjectedHeadcountService.CreateProjectedHeadcount(projectedHeadcount);
                setProjectedHeadcountId(result.data.id); 
                toast.success("Projected Headcount created successfully");
            }
            else {
                projectedHeadcount.id = projectedHeadcountId;
                const result = await ProjectedHeadcountService.UpdateProjectedHeadcount(projectedHeadcount);
                toast.success("Projected Headcount updated successfully");
            }

            setValue("Id", projectedHeadcountId);
            setSelectedMethodId(projectedHeadcount.selectedMethod);           
        }
        catch (error) {
            console.log(error)
        }
        finally{
            setIsLoading(false);
        }
    }

    const setGrowthPercentage = (year: number, growth: number) => {
        const formattedGrowth = decimalFormatter(growth, 2);
        
        if (!isFinite(formattedGrowth as number)) {
            setValue(`expectedPercentageGrowthYear${year}`, 'N/A' as unknown as number, { shouldValidate: true });
        } else {
            setValue(`expectedPercentageGrowthYear${year}`, formattedGrowth as number, { shouldValidate: true });
        }
    };

    const updateFutureGrowthPercentages = (startingYear: number) => {
        for (let y = startingYear + 1; y <= 5; y++) {
            const previous = Number(getValues(`expectedNumberOfEmployeesYear${y - 1}`));
            const current = Number(getValues(`expectedNumberOfEmployeesYear${y}`));

            if (!isNaN(previous) && !isNaN(current)) {
                const updatedGrowth = CalculateYearlyAnnualGrowth(current, previous);
                setGrowthPercentage(y, updatedGrowth);
            }
        }
    };

    const handleFieldChange = (year: number, fieldType: ProjectedHeadcountFieldType, value: number) => {
        const baselineHeadcount = watch(`baselineHeadcount`);
        
        const prevEmployees = year === 1
            ? baselineHeadcount
            : Number(getValues(`expectedNumberOfEmployeesYear${year - 1}`)) || 0;

        let growth = 0;

        if (selectedMethodId === ProjectedHeadcountSelectedMethod.Year5) {
            if (fieldType === ProjectedHeadcountFieldType.Employee) {
                growth = calculateYear5TotalGrowthFromEmployees(value, baselineHeadcount);
                setGrowthPercentage(year, growth);
                
                if (year === 5) {
                    setValue('projectedHeadcount', value, { shouldValidate: true });
                }
            } 
            else if (fieldType === ProjectedHeadcountFieldType.Growth) {
                const newEmployees = calculateYear5NumberOfEmployeesFromTotalGrowth(value, baselineHeadcount);
                const roundedNumberOfEmployees = decimalFormatter(newEmployees, 0);
                setValue(`expectedNumberOfEmployeesYear${year}`, roundedNumberOfEmployees, { shouldValidate: true });

                if (year === 5) {
                    setValue('projectedHeadcount', roundedNumberOfEmployees);
                }
            }

        } 
        else if (selectedMethodId === ProjectedHeadcountSelectedMethod.AnnualGrowthModel) {
            if (fieldType === ProjectedHeadcountFieldType.Employee) {
                const currentEmployees = value;
                const newGrowth = CalculateYearlyAnnualGrowth(currentEmployees, prevEmployees);                
                
                setGrowthPercentage(year, newGrowth);
                updateFutureGrowthPercentages(year);

                if (year === 5) {
                    setValue('projectedHeadcount', currentEmployees);
                }
            }
            else if (fieldType === ProjectedHeadcountFieldType.Growth) {
                const growth = value;
                const newEmployees = CalculateYearlyNumberOfEmployees(growth, prevEmployees);
                const roundedEmployees = decimalFormatter(newEmployees, 0);

                setValue(`expectedNumberOfEmployeesYear${year}`, roundedEmployees, { shouldValidate: true });

                updateFutureGrowthPercentages(year);

                if (year === 5) {
                    setValue('projectedHeadcount', roundedEmployees);
                }            
            }
        }   
    };

    const fetchData = async () => {
        try {
            if (projectedHeadcountId !== undefined) {
                await getProjectedHeadcount(projectedHeadcountId);
            }
            else{
                setValue('baselineHeadcount', props.baselineHeadcount);    
            }
        } catch (error) {
            console.error('Error fetching headcount data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

     useEffect(() => {
         for (let y = 1; y <= 5; y++) {
            handleFieldChange(y, ProjectedHeadcountFieldType.Employee, getValues(`expectedNumberOfEmployeesYear${y}`));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMethodId]);

    return (
        <>
            <Dialog open={true} onClose={props.handleCancel} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={props.handleCancel}>
                    {projectedHeadcountId == undefined ? "Add": "Edit"} {`Projected Headcount ${props.occupationalLevelTitle}`}
                </CustomDialogTitle>
                <DialogContent>
                    {isLoading 
                        ? <SmallLoading/>
                        : (<Box component="form" id='projectedHeadcountsForm' onSubmit={handleSubmit(handleDataSubmit)}>
                            <Grid container>
                                <Grid item xs={12} xl={12}>
                                    <MDBox>
                                        <MDBox mb={2}>
                                            <MDTypography
                                                variant="button"
                                                fontWeight="regular"
                                                style={{ color: '#097384', fontSize: "0.72em" }}
                                            >
                                                Calculations are based on rounded values, and may slightly differ from manual calculations using unrounded values.
                                            </MDTypography>
                                        </MDBox>

                                        <Grid container spacing={2} alignItems="center" mb={2}>
                                            <Grid item xs={6}>
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#000000', fontSize: "0.8em", fontWeight:'bold' }} >
                                                    Baseline headcount
                                                </MDTypography>
                                            </Grid>
                                            <Grid item xs={2}>
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <AppTextInput
                                                        control={control}
                                                        name="baselineHeadcount"
                                                        label=""
                                                        size="small"
                                                        disabled={true}
                                                        InputProps={{
                                                            inputProps: { 
                                                                style: {fontWeight: 'bold'},                                                             
                                                            }
                                                        }}
                                                    />
                                                </MDBox>
                                                {errors.baselineHeadcount && <p>{errors.baselineHeadcount.message}</p>}
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={2} alignItems="center" mb={2}>
                                            <Grid item xs={6}>
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#000000', fontSize: "0.8em" }} >
                                                    Select Method:
                                                </MDTypography>
                                            </Grid>
                                            <Grid item xs={6}>                                    
                                                <Controller
                                                    control={control}
                                                    name="selectedMethod"
                                                    defaultValue={undefined}
                                                    render={({ field: { onChange, value } }) => {
                                                        const selectedOption = selectedMethodOptions.find(
                                                            (option) => option.value === value
                                                        );

                                                        return (
                                                            <Autocomplete
                                                                options={selectedMethodOptions}
                                                                value={selectedOption || null} 
                                                                size="small"
                                                                getOptionLabel={(option) => option.label}
                                                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                onChange={(_, data) => {
                                                                    onChange(data?.value);
                                                                    setSelectedMethodId(data?.value);
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        variant="outlined"
                                                                        error={!!errors.selectedMethod}
                                                                        helperText={errors.selectedMethod?.message}
                                                                        {...params}
                                                                        InputLabelProps={{ shrink: false }}
                                                                    />
                                                                )}
                                                            />
                                                        );
                                                    }}
                                                    />
                                            </Grid>
                                        </Grid>

                                        {selectedMethodId === ProjectedHeadcountSelectedMethod.AnnualGrowthModel &&
                                            <>
                                                {[1, 2, 3, 4, 5].map((year:number) => (
                                                    <YearlyEmployeeInput
                                                        key={year}
                                                        year={year}
                                                        control={control}
                                                        employeeField={`expectedNumberOfEmployeesYear${year}`}
                                                        growthField={`expectedPercentageGrowthYear${year}`}
                                                        selectedMethodId={selectedMethodId}
                                                        onFieldChange={handleFieldChange}
                                                    />
                                                ))}
                                            </>
                                        }

                                        {selectedMethodId === ProjectedHeadcountSelectedMethod.Year5  &&
                                            <>
                                                {[5].map((year:number) => (
                                                    <YearlyEmployeeInput
                                                        key={year}
                                                        year={year}
                                                        control={control}
                                                        employeeField={`expectedNumberOfEmployeesYear${year}`}
                                                        growthField={`expectedPercentageGrowthYear${year}`}
                                                        selectedMethodId={selectedMethodId}
                                                        onFieldChange={handleFieldChange}
                                                    />
                                                ))}
                                            </>
                                        }

                                        <Grid container spacing={2} alignItems="center" mb={2}>
                                            <Grid item xs={6}>
                                                <MDTypography
                                                variant="button"
                                                fontWeight="regular"
                                                style={{ color: '#000000', fontSize: "0.8em", fontWeight:'bold' }}
                                                >
                                                Projected headcount for EE plan
                                                </MDTypography>
                                            </Grid>
                                            <Grid item xs={2}>
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                <AppTextInput
                                                    control={control}
                                                    name="projectedHeadcount"
                                                    label=""
                                                    size="small"
                                                    disabled={true}
                                                    InputProps={{
                                                        inputProps: {
                                                            style: {fontWeight: 'bold'}, 
                                                        }
                                                    }}
                                                />
                                                </MDBox>
                                                {errors.projectedHeadcount && <p>{errors.projectedHeadcount.message}</p>}
                                            </Grid>
                                        </Grid>

                                        <MDBox mt={3} mb={2} className="leftAlign">
                                            <MDTypography variant='button' fontWeight='regular' style={{color:'#666666', fontSize:"0.85em"}}>
                                                Description(Max {commentMaxLength} characters)
                                            </MDTypography>
                                            <Controller
                                                control={control}
                                                name="comment"
                                                render={({ field: { onChange, value } }) => (
                                                    <Quill
                                                        initialContent={value}
                                                        maxLength={commentMaxLength}
                                                        handleChange={(newValue: string) => {
                                                            onChange(newValue);
                                                            setValue('comment', newValue);
                                                        }}
                                                        setRemainingCharacters={setRemainingNoteCharacters}
                                                    />
                                                )}
                                            />
                                            <MDTypography
                                                variant="button"
                                                fontWeight="regular"
                                                style={{  color:
                                                    remainingNoteCharacters < 0
                                                    ? "#FF0000" 
                                                    : "#666666", 
                                                    fontSize: "0.85em"
                                                }}
                                            >
                                                {remainingNoteCharacters} characters remaining
                                            </MDTypography>
                                            {(!!errors.comment) && <FormHelperText error>{errors.comment?.message}</FormHelperText>}
                                        </MDBox>
                                    </MDBox>
                                </Grid>
                            </Grid>
                            {projectedHeadcountId != undefined &&
                                <MDBox mt={2}>
                                    <PortalDocumentsDropZone id={projectedHeadcountId} link={"workforceProfileProjectedHeadcount"}/>
                                </MDBox>
                            }
                        </Box>
                        )
                    }       
                </DialogContent>
                <DialogActions>
                    {!isLoading &&
                        <DialogActions sx={{ display: 'flex', justifyContent: projectedHeadcountId != undefined ? 'space-between' : '', width: '100%', paddingLeft: 0, paddingRight: 0 }}> 
                            <MDButton color="primary" variant="outlined" onClick={props.handleCancel} >
                                Cancel
                            </MDButton>           
                            <MDButton color="primary" variant="contained" type="submit" form='projectedHeadcountsForm'>
                                Save
                            </MDButton>
                        </DialogActions>
                    }
                </DialogActions>
            </Dialog>
        </>
    )
}
